// ADR 23.9.26v §4 — the transport's half of the one online signal.
//
// `navigator.onLine` is true on lie-fi (a captive portal, a dead hotspot), so
// the one `fetch` every JSON call goes through reports its own network failure.
// The wrap sits at `fetch` and nowhere else on purpose: a `TypeError` thrown by
// a `queryFn`'s own code is a bug, not a dead network, and a query-cache
// listener inspecting errors could not tell the two apart.
//
// ADR 2.10.26s §1 — and it is where every request's ceiling lives, because
// only the code that arms the timer can say which abort fired.

import { reportNonFatal } from '../telemetry/capture'
import { reportReachable, reportUnreachable } from './onlineSignal'
import { FOREGROUND_TIMEOUT_MS, routeTemplate } from './requestDeadline'

/**
 * `fetch` itself rejected: the server never saw the request.
 *
 * Distinct from `ApiError` (the server answered) on purpose — `tokenStore`
 * branches on exactly this class to keep a session it would otherwise end
 * (§2), and W3 uses it to tell "keep" from "drop" before any status exists.
 */
export class NetworkUnreachableError extends Error {
  constructor(cause: unknown) {
    super('network unreachable', { cause })
    this.name = 'NetworkUnreachableError'
  }
}

/**
 * ADR 2.10.26s §2 — the request's own deadline fired before a response.
 *
 * A subclass on purpose: to every reader of {@link NetworkUnreachableError}
 * (keep the session, queue the write, serve the cache) a timeout is a dead
 * network. The `name` is set so by-name readers (the embed) and logs keep the
 * distinction.
 */
export class RequestTimeoutError extends NetworkUnreachableError {
  readonly timeoutMs: number

  constructor(timeoutMs: number) {
    super(new Error(`request timed out after ${timeoutMs} ms`))
    this.name = 'RequestTimeoutError'
    this.timeoutMs = timeoutMs
  }
}

function isAbort(error: unknown, init: RequestInit | undefined): boolean {
  if (init?.signal?.aborted === true) return true
  return error instanceof DOMException && error.name === 'AbortError'
}

/** §5 — which single-flight, if any, was holding other callers behind this request. */
function flightOf(template: string): string {
  if (template.endsWith('/auth/refresh')) return 'refresh'
  if (template.endsWith('/device/challenge') || template.endsWith('/device/register')) return 'enrol'
  return 'none'
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  return input instanceof URL ? input.href : input.url
}

function timedOut(input: RequestInfo | URL, timeoutMs: number): RequestTimeoutError {
  const template = routeTemplate(urlOf(input))
  reportNonFatal(`request timed out after ${timeoutMs} ms`, ['RequestTimeoutError', template], {
    timeout_ms: String(timeoutMs),
    flight: flightOf(template),
  })
  reportUnreachable()
  return new RequestTimeoutError(timeoutMs)
}

/**
 * Forward the caller's cancel into the request's own controller; returns the
 * release. An already-aborted signal never fires 'abort' again, so it aborts
 * up front.
 */
function linkCaller(controller: AbortController, caller: AbortSignal | null | undefined): () => void {
  if (caller === null || caller === undefined) return () => {}
  if (caller.aborted) {
    controller.abort(caller.reason)
    return () => {}
  }
  const relay = (): void => controller.abort(caller.reason)
  caller.addEventListener('abort', relay)
  return () => caller.removeEventListener('abort', relay)
}

/** A rejection that is not the deadline's: a cancel or a bug passes through; a `TypeError` is a dead network. */
function rethrown(error: unknown, init: RequestInit | undefined): unknown {
  if (isAbort(error, init) || !(error instanceof TypeError)) return error
  reportUnreachable()
  return new NetworkUnreachableError(error)
}

/**
 * `fetch`, with only the Fetch standard's network error — a `TypeError`
 * rejection — rethrown as {@link NetworkUnreachableError} and reported, and a
 * response that has not arrived within `timeoutMs` rethrown as
 * {@link RequestTimeoutError}. A caller's abort is a cancelled request, not a
 * dead network, and passes through unchanged, as does every response, a 503
 * included.
 *
 * A timer rather than `AbortSignal.timeout` / `AbortSignal.any`: the build
 * floor (Safari 15) has neither, and a missing method on every request's path
 * would turn a ceiling into an outage. The deadline covers `fetch` only —
 * headers, not body.
 */
export async function fetchOrUnreachable(
  input: RequestInfo | URL, init?: RequestInit, timeoutMs = FOREGROUND_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController()
  const release = linkCaller(controller, init?.signal)
  let expired = false
  const timer = setTimeout(() => {
    expired = true
    controller.abort()
  }, timeoutMs)
  try {
    const response = await fetch(input, { ...init, signal: controller.signal })
    reportReachable() // ADR 7.10.26ad §2 — any status: the server answered
    return response
  } catch (error) {
    // Ahead of `isAbort`, which would pass the deadline's own abort through as a cancel.
    throw expired ? timedOut(input, timeoutMs) : rethrown(error, init)
  } finally {
    clearTimeout(timer)
    release()
  }
}
